// Fax cover page for a general fax — any files to any number, tied to a
// client or to no one. Same letterhead and confidentiality notice as the
// physician request letter; one page.
import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, View, Text, styles, COLORS, Letterhead } from './pdfPrimitives';
import { COMPANY } from './letterhead';
import { format } from 'date-fns';

const fmtFax = (d) => { const s = String(d || '').replace(/\D/g, ''); return s.length === 10 ? `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}` : d || ''; };
const Row = ({ label, value }) => (
  <View style={{ flexDirection: 'row', marginBottom: 2 }}>
    <Text style={{ width: 52, fontSize: 8, color: COLORS.mid }}>{label}</Text>
    <Text style={{ flex: 1, fontSize: 9 }}>{value || '—'}</Text>
  </View>
);

function FaxCover({ data }) {
  const { to = {}, from = {}, subject, message, pages, client } = data;
  const paragraphs = String(message || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return (
    <Document title={`Fax – ${subject || ''}`} author={COMPANY.name}>
      <Page size="LETTER" style={[styles.page, { paddingTop: 34, paddingBottom: 50 }]}>
        <View style={{ marginBottom: -8 }}><Letterhead /></View>
        <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 13, color: COLORS.navy, marginBottom: 2 }}>FAX</Text>
        <Text style={{ fontSize: 8.5, color: COLORS.mid, marginBottom: 8 }}>{format(new Date(), 'MMMM d, yyyy')} · {pages ? `${pages} page${pages === 1 ? '' : 's'} including this cover` : 'Cover page'}</Text>

        <View style={{ flexDirection: 'row', borderWidth: 0.5, borderColor: COLORS.border, borderRadius: 3, marginBottom: 8 }}>
          <View style={{ flex: 1, padding: 6, borderRightWidth: 0.5, borderRightColor: COLORS.border }}>
            <Text style={styles.sectionHdr}>TO</Text>
            <Row label="Name" value={to.name} />
            <Row label="Company" value={to.company} />
            <Row label="Fax" value={fmtFax(to.fax)} />
            <Row label="Phone" value={to.phone} />
          </View>
          <View style={{ flex: 1, padding: 6 }}>
            <Text style={styles.sectionHdr}>FROM</Text>
            <Row label="Sender" value={from.name ? `${from.name}${from.title ? `, ${from.title}` : ''}` : COMPANY.name} />
            <Row label="Company" value={COMPANY.name} />
            <Row label="Fax" value={COMPANY.fax} />
            <Row label="Phone" value={from.phone || COMPANY.tel} />
          </View>
        </View>

        <View style={{ backgroundColor: COLORS.grey, padding: 6, marginBottom: 10, borderRadius: 3 }}>
          <Row label="Re" value={subject} />
          {client ? <Row label="Regarding" value={`${client.last_name}, ${client.first_name}${client.date_of_birth ? ` · DOB ${client.date_of_birth}` : ''}${client.medicaid_id ? ` · Medicaid ${client.medicaid_id}` : ''}`} /> : null}
        </View>

        {paragraphs.map((p, i) => <Text key={i} style={[styles.body, { marginBottom: 6 }]}>{p}</Text>)}

        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 12 }}>
          <Text style={styles.body}> </Text>
          <View style={{ width: 220 }}>
            <View style={{ borderTopWidth: 0.8, borderTopColor: '#000', marginTop: 10 }} />
            <Text style={{ fontSize: 9, marginTop: 2 }}>{from.name || ' '}</Text>
            <Text style={{ fontSize: 8, color: COLORS.mid }}>{from.title || 'Care Coordination'} · {COMPANY.name}</Text>
          </View>
        </View>

        <Text fixed style={{ position: 'absolute', bottom: 16, left: 54, right: 54, fontSize: 6.5, color: COLORS.mid, textAlign: 'justify', lineHeight: 1.35 }}>
          CONFIDENTIALITY NOTICE: This facsimile may contain protected health information that is privileged and confidential under HIPAA
          (45 CFR Parts 160 and 164). It is intended only for the recipient named above. If you have received this transmission in error,
          please notify {COMPANY.name} immediately at {COMPANY.tel} and destroy all copies. Any review, disclosure, copying or distribution
          by anyone other than the intended recipient is prohibited.
        </Text>
      </Page>
    </Document>
  );
}

export async function renderFaxCoverBlob(data) {
  return pdf(<FaxCover data={data} />).toBlob();
}
